import type { MemberRole } from './types.js';

/**
 * 방 접근 티켓 (PLAN 0-B 변경 9장, 2026-09-30 확정).
 *
 * 친구 관계가 아니라 **방 단위 접근권**을 관리한다. 티켓 1장 = 1명(이메일 고정),
 * 방장이 발급하고 대상 이메일로 로그인한 사람이 한 번만 쓴다.
 *
 * 이 모듈은 순수 로직만 담는다 — 토큰 문자열 생성은 호출쪽(`ulid`/CSPRNG) 몫이고,
 * 여기서는 "이 티켓이 지금 쓸 수 있는가" 판정만 한다.
 */

export const TICKET_ROLES = ['admin', 'member'] as const;

/** 티켓으로 부여할 수 있는 역할 — owner는 방장 한 명이므로 티켓으로 줄 수 없다 */
export type TicketRole = (typeof TICKET_ROLES)[number];

export type TicketStatus = 'pending' | 'used' | 'revoked';

export type TicketType = 'email' | 'qr';

/** 선택 가능한 만료 기간 (0 = 무제한) */
export const TICKET_TTL_OPTIONS = [0, 86_400, 604_800, 2_592_000] as const;

/** 방장이 한 방에 동시에 띄워 둘 수 있는 미사용 티켓 수 (UX/스팸 가드) */
export const MAX_PENDING_TICKETS = 50;

export interface TicketDoc {
  /** Firestore 문서 ID */
  token: string;
  type?: TicketType;
  /** 발급 대상 이메일(소문자 정규화). QR 초대장의 경우 생략 가능 */
  emailLower?: string;
  role: TicketRole;
  status: TicketStatus;
  maxUses?: number;
  usedCount?: number;
  /** 방장이 적은 메모 */
  note?: string;
  createdBy: string;
  createdAt: number;
  usedBy?: string;
  usedAt?: number;
  /** epoch ms. 없으면 무제한 */
  expiresAt?: number;
  /** 취소 시각 (status==='revoked'일 때) */
  revokedAt?: number;
}

export interface InvitationDoc {
  /** 티켓 토큰 (convoId~secret) */
  token: string;
  convoId: string;
  convoTitle?: string;
  type?: TicketType;
  emailLower?: string;
  role: TicketRole;
  status: TicketStatus;
  maxUses?: number;
  usedCount?: number;
  note?: string;
  createdBy: string;
  createdByName?: string;
  createdByEmail?: string;
  createdAt: number;
  expiresAt?: number;
  usedBy?: string;
  usedAt?: number;
  revokedAt?: number;
}

/** 이메일 형식 검사 —_SPACE/따옴표/연속 점이 섞인 값은 Firebase가 애초에 거절한다 */
const EMAIL_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

/** 최대 길이. Firestore 문서 필드 과당Growth 방지를 위한 상한 */
const MAX_EMAIL_LENGTH = 254;

/**
 * 이메일을 정규화한다 — 트림 + 소문자.
 * Rules의 `lower()` 부재로 클라이언트에서 정규화해 저장하고, 규칙은
 * `auth.token.email`과 문자열 비교만 한다. (Google 이메일은 이미 소문자다)
 */
export function normalizeEmail(raw: string | null | undefined): string {
  return (raw ?? '').trim().toLowerCase();
}

export function isValidEmail(raw: string | null | undefined): boolean {
  const email = normalizeEmail(raw);
  return email.length > 0 && email.length <= MAX_EMAIL_LENGTH && EMAIL_RE.test(email);
}

/** Gmail 계정만 허용 (6~30자 @gmail.com or @googlemail.com) */
const GMAIL_STRICT_RE = /^[a-z0-9](\.?[a-z0-9]){5,29}@gmail\.com$/i;

export function isValidGmail(raw: string | null | undefined): boolean {
  const email = normalizeEmail(raw);
  if (!email || email.length > MAX_EMAIL_LENGTH) return false;
  return (
    GMAIL_STRICT_RE.test(email) ||
    GMAIL_STRICT_RE.test(email.replace(/@googlemail\.com$/, '@gmail.com'))
  );
}

/** 티켓의 role 값이 유효한지 (Rules에 못 넣는 검증을 클라이언트/Functions에서 수행) */
export function isTicketRole(raw: unknown): raw is TicketRole {
  return raw === 'admin' || raw === 'member';
}

/** 만료 시각 epoch ms 계산. 0(무제한)이면 undefined */
export function expiresAtFromTtl(ttlSeconds: number, now: number): number | undefined {
  if (ttlSeconds <= 0) return undefined;
  return now + ttlSeconds * 1000;
}

/** 0이면 "무제한" 라벨용 대표값 — 화면 표기만 담당 */
export function describeTtl(ttlSeconds: number): string {
  switch (ttlSeconds) {
    case 0:
      return 'never';
    case 86_400:
      return 'oneDay';
    case 604_800:
      return 'oneWeek';
    case 2_592_000:
      return 'oneMonth';
    default:
      return String(ttlSeconds);
  }
}

export function isTicketExpired(ticket: Pick<TicketDoc, 'expiresAt'>, now: number): boolean {
  return ticket.expiresAt !== undefined && ticket.expiresAt <= now;
}

/** 아직 아무도 쓰지 않았고 만료되지 않은 티켓인가 */
export function isUsable(ticket: TicketDoc, now: number): boolean {
  return ticket.status === 'pending' && !isTicketExpired(ticket, now);
}

/**
 * 클레임(진입) 사전 검증.
 *
 * 실패 사유를 **열거 불가능하게** 돌려주기보다, 호출부가 그대로 사용자에게 보여줄
 * 메시지를 고를 수 있게 `reason`을 분리한다. 이메일 불일치와 존재하지 않는 티켓은
 * 구분하지 않는다 — 존재 여부 자체가 노출되면 티켓을 훑어볼 수 있다.
 */
export type ClaimFailure =
  | 'not-pending' // 이미 사용됨/취소됨
  | 'expired'
  | 'no-email' // 계정에 이메일이 없다 (익명 로그인)
  | 'email-mismatch';

export type ClaimCheck = { ok: true } | { ok: false; reason: ClaimFailure; maskedEmail?: string };

/**
 * 이메일을 `a***@gmail.com` 형태로 마스킹한다.
 * 티켓 소유자가 누구에게 발급했는지 최소한은 확인돼야 하되 전체 주소는 숨긴다.
 */
export function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return '***';
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length === 1) return `*${domain}`;
  return `${local[0]}${'*'.repeat(Math.min(local.length - 1, 4))}${domain}`;
}

export function checkClaim(
  ticket: TicketDoc | null,
  accountEmail: string | null | undefined,
  now: number,
): ClaimCheck {
  if (!ticket) return { ok: false, reason: 'not-pending' };
  const masked = ticket.emailLower ? maskEmail(ticket.emailLower) : undefined;
  if (ticket.status === 'used' || ticket.status === 'revoked') {
    return { ok: false, reason: 'not-pending', maskedEmail: masked };
  }
  if (isTicketExpired(ticket, now)) {
    return { ok: false, reason: 'expired', maskedEmail: masked };
  }

  // QR 초대장: 특정 이메일에 묶이지 않으므로 이메일 검사를 건너뛰어 누구나(익명 포함) 참여 가능
  if (ticket.type === 'qr') {
    return { ok: true };
  }

  const email = normalizeEmail(accountEmail);
  if (!email) return { ok: false, reason: 'no-email' };
  if (ticket.emailLower && email !== ticket.emailLower) {
    return { ok: false, reason: 'email-mismatch', maskedEmail: masked };
  }
  return { ok: true };
}

/** 티켓이 실제로 부여할 멤버 역할 — owner는 절대 티켓 경유로 생기지 않는다 */
export function roleFromTicket(ticket: TicketDoc): MemberRole {
  return ticket.role === 'admin' ? 'admin' : 'member';
}

/**
 * 방장이 추가로 발급할 수 있는 티켓 수.
 * 이미 사용된 티켓은 자리가 돌아오므로 미사용(pending)만 센다.
 */
export function canIssueMoreTickets(tickets: TicketDoc[]): boolean {
  return tickets.filter((t) => t.status === 'pending').length < MAX_PENDING_TICKETS;
}

/** 방장 화면의 티켓 정렬 — 미사용(만료 임박) → 사용됨 → 취소 */
export function sortTickets(tickets: TicketDoc[], now: number): TicketDoc[] {
  const weight = (t: TicketDoc) => (t.status !== 'pending' ? 2 : isTicketExpired(t, now) ? 1 : 0);
  return [...tickets].sort((a, b) => {
    const w = weight(a) - weight(b);
    if (w !== 0) return w;
    if (weight(a) === 0) return (a.expiresAt ?? Infinity) - (b.expiresAt ?? Infinity);
    return b.createdAt - a.createdAt;
  });
}
